/**
 * Auto-Assignment Service
 *
 * Handles automatic lead assignment when leads are imported with an agent column.
 * This service detects agent columns in CSV data and assigns leads to matching agents
 * in the background after import completes.
 */

import { getApiUrl } from "@/app/_module/services/config";
import supabaseAuthService from "@/app/_module/services/supabaseAuthService";

// Agent column detection patterns (same as BulkAssignmentModal)
// Matches any column containing "agent" or "assigned" keywords
const AGENT_COLUMN_PATTERNS = [
  'agent', 'agent_name', 'agentname', 'agent name',
  'agent_email', 'agentemail', 'agent email',
  'agent_id', 'agentid', 'agent id',
  'assigned_agent', 'assignedagent', 'assigned agent',
  'assigned_to', 'assignedto', 'assigned to', 'assigned',
  'assigned_rep', 'assignedrep', 'assigned rep',
  'rep', 'representative', 'sales_rep', 'salesrep', 'sales rep',
  'owner', 'lead_owner', 'leadowner', 'lead owner',
  'account_owner', 'accountowner', 'account owner',
];

// Columns that LOOK like an agent column but name a PRIOR / EXTERNAL agent, not
// the current assignment. FEX replacement-lead exports carry an "Ex Agent"
// column (the agent on the policy being replaced). Because detection used
// `.includes('agent')`, "Ex Agent" → "ex_agent" matched 'agent' and the import
// auto-assigned leads off the ex-agent's name (e.g. "MARTIN, MARCIA" → the
// downline agent "Martin cukaj"). Never treat these as the assignment column.
const EXCLUDED_AGENT_COLUMN_PATTERNS = [
  'ex_agent', 'exagent', 'ex_agent_name',
  'previous_agent', 'prev_agent', 'prior_agent', 'former_agent',
  'old_agent', 'past_agent', 'writing_agent',
  'previous_owner', 'prior_owner', 'former_owner',
  // ⚠️ DriverIDFK is NOT an agent column. It is a leftover field from a separate
  // logistics/dispatch system and routinely carries a STALE name from an earlier
  // dispatch — the same trap as "Ex Agent", which is why it lives here and not in
  // the match list. It happens to agree with the intended agent on some vendor
  // files, which is exactly what makes it dangerous: it looks reliable until the
  // one file where it isn't, and then leads land on whoever handled the previous
  // run. Never assign from it. Confirmed with the file's owner 2026-09-01.
  'driveridfk',
];

const isExcludedAgentColumn = (normalizedHeader: string): boolean =>
  EXCLUDED_AGENT_COLUMN_PATTERNS.some(
    (ex) => normalizedHeader === ex || normalizedHeader.includes(ex),
  );

interface AgentInfo {
  agentId: string;
  agentName: string;
  email: string;
}

interface AutoAssignmentResult {
  attempted: number;
  successful: number;
  failed: number;
  skipped: number;
  agentColumn: string | null;
}

/**
 * Detects if the CSV headers contain an agent column
 */
export function detectAgentColumn(
  headers: string[],
  rows?: Record<string, unknown>[] | string[][],
): string | null {
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim().replace(/[^a-z0-9_]/g, '_'));

  // ⚠️ A MATCHING HEADER IS NOT ENOUGH — THE COLUMN HAS TO HOLD SOMETHING.
  //
  // Vendor exports routinely carry an `AGENT` column that is present but blank on
  // every row, while the real agent name sits in a differently-named column. This
  // matched on the name alone, picked the empty `AGENT`, resolved nobody, and left
  // the import to fall back on whatever agent was selected in the UI.
  //
  // Measured on 2026-09-01: Moe Ray imported 9 vendor lists in 13 minutes. Every
  // file carried a blank `AGENT` column and the real name in `DriverIDFK`. Two
  // lists whose files said "Mouhamad Baydoun" (2,397 rows) and "Moe Saleh"
  // (2,376 rows) both landed on Moustafa Allabbah — 4,789 leads on the wrong
  // agent, then reported as "already with an agent" when someone tried to fix it.
  //
  // Passing rows lets a populated candidate win over an empty one. Without rows
  // the old header-only behaviour stands, so existing callers are unaffected.
  // Rows arrive in two shapes: keyed records from the import flow, and raw
  // string[][] from the bulk-assignment uploader. Both are accepted so a single
  // matcher can serve every caller — a second copy of this logic is what let the
  // bulk modal drift out of sync and lose the "Ex Agent" exclusion entirely.
  const hasData = (header: string, idx: number): boolean => {
    if (!rows || rows.length === 0) return true; // nothing to judge on — allow it
    const sample = rows.slice(0, 200);
    return sample.some((r) => {
      const cell = Array.isArray(r) ? r[idx] : (r as Record<string, unknown>)?.[header];
      return String(cell ?? '').trim() !== '';
    });
  };

  const matches: Array<{ header: string; idx: number }> = [];
  for (const pattern of AGENT_COLUMN_PATTERNS) {
    const normalizedPattern = pattern.replace(/[^a-z0-9_]/g, '_');
    normalizedHeaders.forEach((h, i) => {
      if (!isExcludedAgentColumn(h) && (h === normalizedPattern || h.includes(normalizedPattern))) {
        if (!matches.some((m) => m.idx === i)) matches.push({ header: headers[i], idx: i });
      }
    });
  }
  if (matches.length === 0) return null;

  // Pattern order is priority order, so the first POPULATED match wins.
  const populated = matches.find((m) => hasData(m.header, m.idx));
  if (populated) return populated.header;

  // Every candidate is blank. Report NO agent column rather than naming an empty
  // one: a header with nothing under it cannot assign anybody, and returning it
  // made the import screen announce "agent column detected" for a file that would
  // assign nobody — then quietly fall through to whatever agent was left selected
  // in the UI. These vendor files are exactly that shape (blank `AGENT`, with the
  // only populated candidate being the blacklisted DriverIDFK), so the honest
  // answer is: no agent column, import unassigned, let the owner choose.
  //
  // Callers that pass no rows still get the old header-only behaviour, since
  // hasData() returns true when there is nothing to judge on.
  return rows && rows.length > 0 ? null : matches[0].header;
}

/**
 * Fetches available agents from the agency workload endpoint
 */
// Exported so the import screen can PREVIEW who a file will assign to, before
// the import runs. Every bug in this flow so far has been silent — the import
// said "completed" and quietly put thousands of leads on the wrong agent.
export async function fetchAgents(agencyId?: string): Promise<AgentInfo[]> {
  try {
    const session = await supabaseAuthService.getCurrentSession();
    const token = session?.access_token;
    if (!token) {
      return [];
    }

    let currentAgencyId = agencyId;

    // If no agency ID provided, try to get it from user profile
    if (!currentAgencyId) {
      const userResponse = await fetch(getApiUrl('users/profile'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!userResponse.ok) {
        return [];
      }

      const userData = await userResponse.json();
      // Try multiple paths to find agency ID
      currentAgencyId = userData.data?.activeAgencyId
        || userData.data?.agencyId
        || userData.data?.currentAgency?.agency?.id
        || userData.activeAgencyId
        || userData.agencyId;
    }

    if (!currentAgencyId) {
      return [];
    }

    // Fetch agents from the agency
    const response = await fetch(getApiUrl(`agencies/${currentAgencyId}/agents/workload?limit=1000`), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return [];
    }

    const result = await response.json();
    // Handle multiple data structures
    const agents = result.data?.items || result.data?.agents || result.items || result.agents || [];

    return agents.map((a: any) => ({
      agentId: a.agentId || a.id,
      agentName: a.agentName || a.name || '',
      email: a.email || '',
    }));
  } catch {
    return [];
  }
}

/**
 * Matches a CSV agent value to a system agent
 * Priority: 1) Email match, 2) Exact name match, 3) Partial name match
 */
// Exported so the agent-resolution rules can be smoke-tested against the real
// roster. Step 3 below is a fuzzy partial match, and this book contains Moe Ray,
// Moe Saleh, Mo Baydoun, Mouhamad Baydoun and Moustafa Allabbah — near-collisions
// that decide who owns thousands of leads, so it needs to be testable.
export function matchAgent(csvAgentValue: string, agents: AgentInfo[]): AgentInfo | null {
  if (!csvAgentValue || !agents.length) return null;

  const normalized = csvAgentValue.toLowerCase().trim();

  // 1. Email match first (highest priority)
  for (const agent of agents) {
    if (agent.email && agent.email.toLowerCase().trim() === normalized) {
      return agent;
    }
  }

  // 2. Exact name match
  for (const agent of agents) {
    if (agent.agentName.toLowerCase().trim() === normalized) {
      return agent;
    }
  }

  // 3. Partial name match — SCORED, and refuses to guess when it is ambiguous.
  //
  // ⚠️ TWO BUGS LIVED HERE. Both assigned thousands of leads to the wrong person.
  //
  // (a) `part.length >= 3` guarded only the CSV side. The AGENT's name parts had
  //     no minimum, so a two-letter name matched any word containing it: the
  //     roster's "Al Capital" won "Moustafa Allabah", because "allabah".includes("al").
  //     Measured 2026-09-01 against the real 172-person MRFG roster.
  //
  // (b) It returned the FIRST agent that matched at all. Roster order decided the
  //     owner, so "Moe Saleh" could land on "Moe Ray" purely because Moe Ray was
  //     listed earlier — two different people, thousands of leads.
  //
  // Both sides now need 3+ characters, every candidate is scored by how many name
  // parts it matches, and a tie returns null. Declining is deliberate: an
  // unassigned lead is visible and fixable in one click, while a lead silently
  // assigned to the wrong agent is invisible until someone works the wrong book.
  const nameParts = normalized.split(/\s+/).filter(p => p.length >= 3);
  if (nameParts.length === 0) return null;

  let best: { agent: AgentInfo; score: number } | null = null;
  let tied = false;

  for (const agent of agents) {
    const agentParts = agent.agentName.toLowerCase().split(/\s+/).filter(p => p.length >= 3);
    let score = 0;
    for (const part of nameParts) {
      // Exact part equality is worth more than containment, so "saleh" == "saleh"
      // outranks a loose substring hit elsewhere in the roster.
      if (agentParts.some(ap => ap === part)) score += 2;
      else if (agentParts.some(ap => ap.includes(part) || part.includes(ap))) score += 1;
    }
    if (score === 0) continue;
    if (!best || score > best.score) {
      best = { agent, score };
      tied = false;
    } else if (score === best.score) {
      tied = true;
    }
  }

  return best && !tied ? best.agent : null;
}

/**
 * Assigns leads to an agent via the API
 * Uses sendBeacon as primary method for reliable delivery during navigation
 * Falls back to fetch with keepalive if sendBeacon fails
 */
async function assignLeadsToAgent(leadIds: string[], agentId: string): Promise<number> {
  try {
    const session = await supabaseAuthService.getCurrentSession();
    const token = session?.access_token;
    if (!token) {
      return 0;
    }

    const url = getApiUrl('lead-assignment/assign');
    // 1,000 per request: browsers cap keepalive bodies at 64 KB (~1,600 ids), and a
    // whole agent's group in one call got rejected over the backend's per-request cap.
    const CHUNK = 1000;
    let assigned = 0;
    for (let i = 0; i < leadIds.length; i += CHUNK) {
      const slice = leadIds.slice(i, i + CHUNK);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ leadIds: slice, agentId }),
          keepalive: true,
          signal: controller.signal,
        });
        // The API answers HTTP 200 with the real status in the body (e.g. 400 over
        // the cap), so response.ok alone counted rejected chunks as assigned.
        const body = await response.json().catch(() => null);
        if (response.ok && !(typeof body?.statusCode === 'number' && body.statusCode >= 300)) {
          assigned += slice.length;
        }
      } catch {
        // counted as not assigned
      } finally {
        clearTimeout(timeoutId);
      }
    }
    return assigned;
  } catch {
    return 0;
  }
}

/**
 * Fetches recently imported leads that are unassigned
 * Uses the lead IDs from the import results
 */
async function fetchRecentlyImportedLeads(leadIds: string[]): Promise<Map<string, any>> {
  const leadsMap = new Map<string, any>();

  try {
    const session = await supabaseAuthService.getCurrentSession();
    const token = session?.access_token;
    if (!token) {
      return leadsMap;
    }

    // Fetch unassigned leads
    const response = await fetch(getApiUrl('lead-assignment/unassigned?limit=1000'), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return leadsMap;
    }

    const result = await response.json();
    const leads = result.data?.items || result.data?.unassignedLeads || result.data?.leads || [];

    // Only include leads that were just imported
    const importedSet = new Set(leadIds);
    for (const lead of leads) {
      if (importedSet.has(lead.id)) {
        leadsMap.set(lead.id, lead);
      }
    }

    return leadsMap;
  } catch {
    return leadsMap;
  }
}

/**
 * Main function to auto-assign leads after import
 *
 * @param csvData - The original CSV data with agent column values
 * @param headers - CSV headers
 * @param importedLeadIds - IDs of successfully imported leads
 * @param mappings - Field mappings from the import
 */
export async function autoAssignLeadsAfterImport(
  csvData: Record<string, unknown>[],
  headers: string[],
  importedLeadIds: string[],
  mappings: Record<string, string>
): Promise<AutoAssignmentResult> {
  const result: AutoAssignmentResult = {
    attempted: 0,
    successful: 0,
    failed: 0,
    skipped: 0,
    agentColumn: null,
  };

  // Rows are passed so an empty `AGENT` column cannot beat a populated one.
  const agentColumn = detectAgentColumn(headers, csvData);
  if (!agentColumn) {
    return result;
  }

  result.agentColumn = agentColumn;

  // Fetch available agents
  const agents = await fetchAgents();
  if (agents.length === 0) {
    return result;
  }

  // Fetch recently imported leads
  const importedLeads = await fetchRecentlyImportedLeads(importedLeadIds);

  // Build mapping from CSV row to agent
  // We need to match CSV rows to imported leads by email/phone/name
  const agentAssignments = new Map<string, string[]>(); // agentId -> leadIds

  // Get the mapped fields for matching
  const emailField = Object.entries(mappings).find(([k]) => k === 'email')?.[1];
  const phoneField = Object.entries(mappings).find(([k]) => k === 'phone')?.[1];
  const nameField = Object.entries(mappings).find(([k]) => k === 'name')?.[1];
  const firstNameField = Object.entries(mappings).find(([k]) => k === 'firstName')?.[1];
  const lastNameField = Object.entries(mappings).find(([k]) => k === 'lastName')?.[1];

  for (const row of csvData) {
    const agentValue = String(row[agentColumn] || '').trim();
    if (!agentValue) {
      result.skipped++;
      continue;
    }

    // Match agent
    const matchedAgent = matchAgent(agentValue, agents);
    if (!matchedAgent) {
      result.skipped++;
      continue;
    }

    // Find the matching lead
    let matchedLeadId: string | null = null;

    // Match by email
    if (emailField) {
      const csvEmail = String(row[emailField] || '').toLowerCase().trim();
      if (csvEmail) {
        for (const [leadId, lead] of importedLeads) {
          if (lead.email?.toLowerCase().trim() === csvEmail) {
            matchedLeadId = leadId;
            break;
          }
        }
      }
    }

    // Match by phone if no email match
    if (!matchedLeadId && phoneField) {
      const csvPhone = String(row[phoneField] || '').replace(/\D/g, '').slice(-10);
      if (csvPhone && csvPhone.length >= 7) {
        for (const [leadId, lead] of importedLeads) {
          const leadPhone = String(lead.phone || '').replace(/\D/g, '').slice(-10);
          if (leadPhone && leadPhone.length >= 7 && leadPhone === csvPhone) {
            matchedLeadId = leadId;
            break;
          }
        }
      }
    }

    // Match by name if no email/phone match
    if (!matchedLeadId) {
      let csvName = '';
      if (nameField) {
        csvName = String(row[nameField] || '').toLowerCase().trim();
      } else if (firstNameField || lastNameField) {
        const firstName = String(row[firstNameField || ''] || '').trim();
        const lastName = String(row[lastNameField || ''] || '').trim();
        csvName = `${firstName} ${lastName}`.toLowerCase().trim();
      }

      if (csvName) {
        for (const [leadId, lead] of importedLeads) {
          if (lead.name?.toLowerCase().trim() === csvName) {
            matchedLeadId = leadId;
            break;
          }
        }
      }
    }

    if (matchedLeadId) {
      result.attempted++;
      if (!agentAssignments.has(matchedAgent.agentId)) {
        agentAssignments.set(matchedAgent.agentId, []);
      }
      agentAssignments.get(matchedAgent.agentId)!.push(matchedLeadId);
      // Remove from map to prevent duplicate assignments
      importedLeads.delete(matchedLeadId);
    } else {
      result.skipped++;
    }
  }

  // Perform assignments in batches per agent
  for (const [agentId, leadIds] of agentAssignments) {
    const assigned = await assignLeadsToAgent(leadIds, agentId);
    result.successful += assigned;
    result.failed += leadIds.length - assigned;
  }

  return result;
}

export default {
  detectAgentColumn,
  autoAssignLeadsAfterImport,
  fetchAgents,
  matchAgent,
};
